import React, { Fragment, useMemo } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import Checkbox from '../../common/Checkbox';
import FormButtons from './components/FormButtons';
import DocumentsSection from './components/DocumentsSection';
import AdditionalInsurancesSection from './components/AdditionalInsurancesSection';
import MainInsuranceFormSection from '../../ProfileLayout/InsuranceDetails/MainInsuranceFormSection';
import UploadingInsuranceCardModal from '../../ProfileLayout/InsuranceDetails/UploadingInsuranceCardModal';
import PrimarySubscriberFormSection from '../../ProfileLayout/InsuranceDetails/PrimarySubscriberFormSection';
import HandleErrorCheckProfileInsuranceModal from '../../ProfileLayout/InsuranceDetails/HandleErrorCheckProfileInsuranceModal';

import useUserDocuments from '../../Paperwork/hooks/useUserDocuments';
import useHealthPlanList from '../../../hooks/selectOptions/useHealthPlanList';
import useInsuranceFormManager from '../../../hooks/patientInsurance/useInsuranceFormManager';
import useAdditionalInsurance from '../../../hooks/patientInsurance/useAdditionalInsurance';
import useIsAdditionalInsurancesEnabledAndMaxNumber from '../../../hooks/patientInsurance/useIsAdditionalInsurancesEnabledAndMaxNumber';

import {
  INSURANCE_FLOW_TYPE,
  SHOW_ADDITIONAL_STYLES_FOR_ADDITIONAL_INSURANCES,
} from '../../../constants/insurance-flow-type';
import { INSURANCE_ELIGIBILITY_FIELDS_TYPES } from '../../../constants/formFields';

import { selectUploadInsuranceCardModalData } from '../../../redux/manage-insurances/selectors/common';
import { manageInsurancesActions } from '../../../redux/manage-insurances/actions/manage-insurances-actions';

import { getAdditionalInsuranceCardDocumentsConfig } from '../../../utils/getDocumentsConfig';

import styles from './index.styl';
import { paperworkCommonActions } from '../../../redux/paperwork/actions/common';

const {
  PRIMARY_SUBSCRIBER,
  DO_NOT_HAVE_INSURANCE,
  HEALTH_PLAN,
  MEMBER_ID,
} = INSURANCE_ELIGIBILITY_FIELDS_TYPES;

export const hasCardSideValues = side =>
  Object.values(side || {}).filter(Boolean).length > 0;

const InsuranceForm = ({
  cancel,
  flowType,
  insuranceData,
  navigateToNextPage,
  taskAssignmentId,
  userId,
  userName,
  withSelfPayEnding,
  withPlanNotListedOption = true,
  allowToCompleteWithoutFileUpload,
}) => {
  const dispatch = useDispatch();

  const { formatMessage } = useIntl();

  const {
    entities,
    handleInputChange: handleHealthPlanInputChange,
    handleMenuScrollToBottom: handleHealthPlanMenuScrollToBottom,
    isLoading: isHealthPlanLoading,
  } = useHealthPlanList(withPlanNotListedOption);

  const uploadInsuranceCardModalData = useSelector(
    selectUploadInsuranceCardModalData,
  );

  const {
    isAdditionalInsurancesEnabled,
  } = useIsAdditionalInsurancesEnabledAndMaxNumber();

  const {
    driverLicenseDocuments,
    driverLicenseDocumentsConfig,
    insuranceDocuments,
    insuranceDocumentsConfig,
  } = useUserDocuments({ isEditMode: true });

  const {
    formErrors,
    formState,
    handleChangeField,
    handleFormChange,
    handleHealthPlanChange,
    haveInsuranceCheckboxLabel,
    isPrimarySubscriber,
    isValid,
    onHaveInsuranceChange,
    onPrimarySubscriberChange,
    primarySubscriberCheckboxLabel,
    userHasNotInsurance,
    validateFormFields,
  } = useInsuranceFormManager({
    insuranceData,
    userId,
    userName,
    withSelfPayEnding,
    flowType,
  });

  const frontSide = get(formState, 'frontSide', null);
  const backSide = get(formState, 'backSide', null);
  const healthPlan = get(formState, 'healthPlan', null);

  const isMainInsuranceSideCardsUploaded =
    hasCardSideValues(frontSide) && hasCardSideValues(backSide);

  const {
    validateAdditionalInsurancesFormFields,
    isAdditionalInsurancesFormsValid,
    ...additionalInsurancesProps
  } = useAdditionalInsurance({
    isPrimarySubscriberDefault: isPrimarySubscriber,
  });

  const insuranceSectionTitle = formatMessage({ id: 'profile.insurance' });

  const isPlanNotListedSelected = Boolean(healthPlan && !healthPlan.id);

  const showInsuranceSectionTitle = !SHOW_ADDITIONAL_STYLES_FOR_ADDITIONAL_INSURANCES.includes(
    flowType,
  );

  const showDocumentsSection = !SHOW_ADDITIONAL_STYLES_FOR_ADDITIONAL_INSURANCES.includes(
    flowType,
  );

  const isProfileFlow = flowType === INSURANCE_FLOW_TYPE.PROFILE;

  const isToDoFlow = flowType === INSURANCE_FLOW_TYPE.TO_DO;

  const additionalInsurancesState =
    additionalInsurancesProps.additionalInsurances;

  const allowToSkipMainInsurance = useMemo(() => {
    return (
      [MEMBER_ID, HEALTH_PLAN].every(key => !formState[key]) &&
      formState[PRIMARY_SUBSCRIBER]
    );
  }, [formState]);

  const {
    mainInsuranceDocuments,
    mainInsuranceDocumentsConfig,
  } = useMemo(() => {
    if (isToDoFlow) {
      const fieldName = 'toDoInsuranceCard';

      const documents = [
        {
          key: `${fieldName}_1`,
          attachment: frontSide,
        },

        {
          key: `${fieldName}_2`,
          attachment: backSide,
        },
      ];

      const documentsConfig = getAdditionalInsuranceCardDocumentsConfig({
        formatMessage,
        userDocuments: documents,
        // used to add (optional) placeholder to section title
        isEditMode: allowToCompleteWithoutFileUpload,
        fieldName,
      });

      return {
        mainInsuranceDocuments: documents,
        mainInsuranceDocumentsConfig: documentsConfig,
      };
    }

    return {
      mainInsuranceDocuments: insuranceDocuments,
      mainInsuranceDocumentsConfig: insuranceDocumentsConfig,
    };
  }, [
    insuranceDocuments,
    insuranceDocumentsConfig,
    frontSide,
    backSide,
    isToDoFlow,
    allowToCompleteWithoutFileUpload,
  ]);

  const disableSubmitButton = useMemo(() => {
    if (userHasNotInsurance) return false;

    const isMainFormValid = isPlanNotListedSelected ? true : isValid;

    if (!isAdditionalInsurancesEnabled) {
      return !isMainFormValid;
    }

    if (isToDoFlow) {
      return (
        !isMainFormValid ||
        !isAdditionalInsurancesFormsValid ||
        (!isMainInsuranceSideCardsUploaded && !allowToCompleteWithoutFileUpload)
      );
    }

    if (allowToSkipMainInsurance && additionalInsurancesState.length) {
      return !isAdditionalInsurancesFormsValid;
    }

    return !isMainFormValid || !isAdditionalInsurancesFormsValid;
  }, [
    additionalInsurancesState,
    allowToSkipMainInsurance,
    isAdditionalInsurancesEnabled,
    isAdditionalInsurancesFormsValid,
    isToDoFlow,
    isValid,
    userHasNotInsurance,
    isPlanNotListedSelected,
    isMainInsuranceSideCardsUploaded,
    allowToCompleteWithoutFileUpload,
  ]);

  const finishAction = useMemo(() => {
    if (flowType === INSURANCE_FLOW_TYPE.PAPERWORK) {
      dispatch(
        paperworkCommonActions.setPaperworkInsuranceIsPlanNotListedSelected(
          isPlanNotListedSelected,
        ),
      );
    }

    switch (flowType) {
      case INSURANCE_FLOW_TYPE.PROFILE:
      case INSURANCE_FLOW_TYPE.TO_DO: {
        return cancel;
      }

      case INSURANCE_FLOW_TYPE.PAPERWORK:
      case INSURANCE_FLOW_TYPE.GET_CARE_FLOW: {
        return navigateToNextPage;
      }

      default: {
        return null;
      }
    }
  }, [flowType, navigateToNextPage, cancel, isPlanNotListedSelected]);

  const onImageChange = (fieldName, imgValue, imgId) => {
    const payload = {
      fieldName,
      imgId,
      imgValue,
      userId,
    };

    const patientInsuranceId = get(insuranceData, 'id', null);

    if (isToDoFlow) {
      dispatch(
        manageInsurancesActions.uploadInsuranceCard({
          ...payload,
          patientInsuranceId,
          handleAdditionalInsuranceFormChange: handleFormChange,
          taskAssignmentId,
        }),
      );
    } else {
      dispatch(
        manageInsurancesActions.uploadMainInsuranceCard({
          ...payload,
          handleFormChange,
        }),
      );
    }
  };

  const saveInsurancesData = (saveAnyway = false) => {
    let areAdditionalInsurancesValid = true;

    const { additionalInsurances } = additionalInsurancesProps;

    const { isValid: isMainFormValid } = userHasNotInsurance
      ? { isValid: true }
      : validateFormFields(formState);

    if (isAdditionalInsurancesEnabled) {
      additionalInsurances.forEach((insurance, index) => {
        const { isValid: isFormValid } = validateAdditionalInsurancesFormFields(
          insurance,
          index,
        );

        if (!isFormValid) {
          areAdditionalInsurancesValid = false;
        }
      });
    }

    if (!areAdditionalInsurancesValid || !isMainFormValid) return;

    const additionalInsurancesFormState = additionalInsurances.map(
      ({ formState: insurancesFormState }) => insurancesFormState,
    );

    if (isToDoFlow) {
      dispatch(
        manageInsurancesActions.saveToDoInsurancesData({
          mainInsuranceFormState: formState,
          additionalInsurancesFormState,
          userId,
          taskAssignmentId,
          finishAction,
          saveAnyway,
        }),
      );
    } else {
      dispatch(
        manageInsurancesActions.savePatientInsurances({
          flowType,
          mainInsuranceFormState: formState,
          additionalInsurancesFormState,
          userId,
          finishAction,
        }),
      );
    }
  };

  const onFormSubmit = e => {
    e.preventDefault();

    saveInsurancesData();
  };

  return (
    <Fragment>
      {showDocumentsSection && (
        <section className={styles.driverLicenseSection}>
          <div className={styles.driverLicenseSectionTitle}>
            {driverLicenseDocumentsConfig.title}
          </div>
          <DocumentsSection
            config={driverLicenseDocumentsConfig}
            documents={driverLicenseDocuments}
            onImageChange={onImageChange}
          />
        </section>
      )}

      <form onSubmit={onFormSubmit}>
        {showInsuranceSectionTitle && (
          <div className={styles.insuranceDocumentsSectionTitle}>
            {insuranceSectionTitle}
          </div>
        )}

        <div
          className={cn(styles.checkbox, styles.patientInsuranceCheckbox, {
            [styles.patientInsuranceCheckbox_profile]: isProfileFlow,
          })}
        >
          <Checkbox
            size={16}
            label={haveInsuranceCheckboxLabel}
            id={DO_NOT_HAVE_INSURANCE}
            onChange={onHaveInsuranceChange}
            checked={Boolean(userHasNotInsurance)}
          />
        </div>

        {!userHasNotInsurance && (
          <Fragment>
            <section
              className={cn(styles.insuranceFormSection, {
                [styles.insuranceFormSection_additionalStyles]: !showInsuranceSectionTitle,
              })}
            >
              <DocumentsSection
                config={mainInsuranceDocumentsConfig}
                documents={mainInsuranceDocuments}
                onImageChange={onImageChange}
              />

              <MainInsuranceFormSection
                formState={formState}
                isPlanNotListedSelected={isPlanNotListedSelected}
                handleHealthPlanChange={handleHealthPlanChange}
                entities={entities}
                handleInputChange={handleHealthPlanInputChange}
                handleMenuScrollToBottom={handleHealthPlanMenuScrollToBottom}
                isLoading={isHealthPlanLoading}
                handleFormChange={handleFormChange}
              />

              <div className={styles.primarySubscriberCheckbox}>
                <Checkbox
                  size={16}
                  disabled={isPlanNotListedSelected}
                  label={primarySubscriberCheckboxLabel}
                  id={PRIMARY_SUBSCRIBER}
                  checked={Boolean(isPrimarySubscriber)}
                  onChange={onPrimarySubscriberChange}
                />
              </div>

              {!isPrimarySubscriber && (
                <PrimarySubscriberFormSection
                  formErrors={formErrors}
                  formState={formState}
                  handleChangeField={handleChangeField}
                  handleFormChange={handleFormChange}
                />
              )}
            </section>

            {isAdditionalInsurancesEnabled && (
              <AdditionalInsurancesSection
                {...additionalInsurancesProps}
                flowType={flowType}
                entities={entities}
                handleInputChange={handleHealthPlanInputChange}
                handleMenuScrollToBottom={handleHealthPlanMenuScrollToBottom}
                isLoading={isHealthPlanLoading}
                primarySubscriberCheckboxLabel={primarySubscriberCheckboxLabel}
                taskAssignmentId={taskAssignmentId}
                userId={userId}
                userName={userName}
              />
            )}
          </Fragment>
        )}

        <FormButtons
          flowType={flowType}
          cancel={cancel}
          disableSubmitButton={disableSubmitButton}
        />
      </form>

      <UploadingInsuranceCardModal {...uploadInsuranceCardModalData} />

      <HandleErrorCheckProfileInsuranceModal
        userId={userId}
        skipInsurance={userHasNotInsurance}
        finishAction={finishAction}
        flowType={flowType}
        handleSave={saveInsurancesData}
      />
    </Fragment>
  );
};

InsuranceForm.propTypes = {
  allowToCompleteWithoutFileUpload: PropTypes.bool,
  cancel: PropTypes.func,
  flowType: PropTypes.oneOf(Object.keys(INSURANCE_FLOW_TYPE)).isRequired,
  insuranceData: PropTypes.object,
  navigateToNextPage: PropTypes.func,
  taskAssignmentId: PropTypes.number,
  userId: PropTypes.number,
  userName: PropTypes.string,
  withSelfPayEnding: PropTypes.bool,
  withPlanNotListedOption: PropTypes.bool,
};

export default InsuranceForm;



// WEBPACK FOOTER //
// ./src/components/forms/InsuranceForm/index.jsx